import { useEffect, useState, useRef } from "react";
import { COMPLETED_PROJECTS } from "../../lib/data";

/**
 * CompletedProjects — "Built by Amarja"
 * Cinematic ultra-premium auto-advancing showcase. One villa at a time.
 * No arrows, no 01/07 counter. Just elegant thumbnails + slow Ken Burns + crossfade.
 * Villa photography uses object-contain so images are NEVER cropped.
 */
export default function CompletedProjects() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0); // changes when user manually advances
  const intervalRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % COMPLETED_PROJECTS.length);
    }, 6500);
    return () => clearInterval(intervalRef.current);
  }, [paused, tick]);

  const goto = (i) => {
    setIndex(i);
    setTick((t) => t + 1); // reset interval so next auto-advance is a full 6.5s away
  };

  return (
    <section
      id="projects"
      data-testid="completed-projects-section"
      className="relative bg-[#F7F5F0] text-[#1A362D] py-20 md:py-28 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[110rem] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Header */}
        <div className="mb-10 md:mb-16">
          <p
            data-testid="gated-community-line"
            className="text-[11px] sm:text-xs tracking-[0.32em] uppercase text-[#987F53] font-semibold mb-6"
          >
            A Gated Community · Where the Rich &amp; Famous Are Your Neighbours
          </p>
          <h2 className="reveal font-serif-display text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight font-light text-[#1A362D]">
            Built by <em className="font-normal">Amarja.</em>
          </h2>
        </div>

        {/* Cinematic showcase — clean white backdrop, NO name caption */}
        <div
          data-testid="projects-stage"
          className="relative w-full bg-[#FFFCF6] overflow-hidden"
        >
          <div className="relative aspect-[3/2] sm:aspect-[16/10] md:aspect-[16/9]">
            {COMPLETED_PROJECTS.map((p, i) => (
              <div
                key={p.id}
                data-testid={`project-${p.id}`}
                className={`absolute inset-0 transition-opacity duration-[1600ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
                  i === index ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
                aria-hidden={i !== index}
              >
                <img
                  src={p.image}
                  alt=""
                  className={`w-full h-full object-contain transition-transform ease-out ${
                    i === index ? "scale-[1.04]" : "scale-100"
                  }`}
                  style={{ transitionDuration: "7000ms" }}
                  loading={i < 2 ? "eager" : "lazy"}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Thumbnail strip — no arrows, no counter, just elegant nav */}
        <div
          data-testid="projects-thumbs"
          className="mt-6 md:mt-8 grid grid-cols-7 gap-2 sm:gap-3"
        >
          {COMPLETED_PROJECTS.map((p, i) => (
            <button
              key={p.id}
              data-testid={`project-thumb-${p.id}`}
              onClick={() => goto(i)}
              aria-label={`View ${p.name}`}
              className={`relative aspect-[3/2] overflow-hidden bg-[#EBE7DF] transition-all duration-500 ${
                i === index
                  ? "ring-2 ring-[#1A362D] ring-offset-2 ring-offset-[#F7F5F0]"
                  : "opacity-55 hover:opacity-100"
              }`}
            >
              <img
                src={p.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
